import type React from "react"

import {
  getMuiTextFieldVariant,
  getStackedFormControlClassName,
} from "@/styles/form-field-variant"

import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material"

import { getSelectInput } from "../inputs-utils"
import type { SelectFieldProps } from "./select-field.types"

/**
 * Select field that composes formcontrol, helpertext, formlabel, the same way text field does
 *
 * ```tsx
 * <SelectField
 *   name="test"
 *   options={[{ value: 'value 1', label: 'label 1}]}
 *   onChange={onChange}
 *   value='value 1'
 * />
 * ```
 */
export const SelectField = ({
  disabled,
  error,
  fullWidth,
  helperText,
  id,
  inputRef,
  label,
  multiple,
  name,
  options,
  placeholder,
  renderOption,
  renderValue,
  required,
  sx,
  value,
  variant,
  ...rest
}: SelectFieldProps) => {
  let renderValueFn: (value: unknown) => React.ReactNode = (
    selected: unknown
  ) => {
    if (!selected && placeholder) {
      return <Box component="em">{placeholder}</Box>
    }
    if (
      selected &&
      Array.isArray(selected) &&
      selected.length === 0 &&
      placeholder
    ) {
      return <Box component="em">{placeholder}</Box>
    }
    if (!selected && !placeholder) {
      return ""
    }

    if (multiple && Array.isArray(selected)) {
      return selected
        .map((val) => options.find((option) => option.value === val)?.label)
        .join(", ")
    }

    return options.find((option) => option.value === selected)?.label
  }

  if (renderValue) {
    renderValueFn = renderValue
  }

  const muiVariant = getMuiTextFieldVariant(variant) ?? "outlined"

  return (
    <FormControl
      className={getStackedFormControlClassName(variant)}
      disabled={disabled}
      error={error}
      fullWidth={fullWidth}
      sx={sx}
      variant={muiVariant}
    >
      {label && (
        <InputLabel
          data-testid={id ?? name}
          id={id ?? name}
          required={required}
        >
          {label}
        </InputLabel>
      )}
      <Select
        {...rest}
        displayEmpty
        id={`${id ?? name}-select`}
        input={getSelectInput(variant, label)}
        inputRef={inputRef}
        labelId={id ?? name}
        multiple={multiple}
        name={name}
        renderValue={renderValueFn}
        value={value}
        variant={muiVariant}
      >
        {placeholder ? (
          <MenuItem disabled value="">
            <Box component="em">{placeholder}</Box>
          </MenuItem>
        ) : null}

        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {renderOption ? renderOption(option) : option.label}
          </MenuItem>
        ))}
      </Select>
      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
    </FormControl>
  )
}
